import { BASE_BACKEND_URL } from '@/requests/constants';
import { getHeaders } from '@/requests/headers';
import { DateTime } from 'luxon';

import { usePerms } from '@/hooks/usePerms';
import { useSession } from '@/providers/session-provider';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

type FailedInvoiceIds = Array<{ object_id: string }>;
type CountResponse = { count: number; identifier?: string };

export function useFailedInvoices(): UseQueryResult<FailedInvoiceIds, Error>;
export function useFailedInvoices(
  countOnly: true
): UseQueryResult<CountResponse, Error>;
export function useFailedInvoices(
  countOnly?: boolean
): UseQueryResult<FailedInvoiceIds | CountResponse, Error> {
  const {
    data: {
      session: { organizationId },
    },
  } = useSession();
  const { canView } = usePerms('invoice');

  return useQuery(
    failedInvoices({ countOnly, organizationId, enabled: canView })
  );
}

export function failedInvoices({
  countOnly,
  identifier = '',
  enabled,
  organizationId,
}: {
  countOnly?: boolean;
  organizationId?: string;
  identifier?: string;
  enabled?: boolean;
}): {
  queryKey: (string | boolean | undefined | { organization: string })[];
  queryFn: () => Promise<FailedInvoiceIds | CountResponse>;
  enabled?: boolean;
} {
  return {
    queryKey: [
      'failedInvoices',
      countOnly,
      organizationId ? { organization: organizationId } : undefined,
      identifier,
    ],
    queryFn: async (): Promise<FailedInvoiceIds | CountResponse> => {
      const params = {
        ...(organizationId ? { organization: organizationId } : {}),
        content_type: 'accounting.invoice',
        start_time: DateTime.now()
          .minus({ month: 1 })
          .startOf('day')
          .toISODate(),
        end_time: DateTime.now().plus({ days: 1 }).startOf('day').toISODate(),
        event_identifier: 'accounting.invoice.invoiceattested',
        object_filter_data: JSON.stringify({ closed_time__isnull: true }),
      };

      const { headers } = getHeaders();
      const response = await fetch(
        `${BASE_BACKEND_URL}/communication/failed_notification_deliveries/?` +
          new URLSearchParams(params).toString(),
        {
          method: 'GET',
          headers: headers,
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch failed deliveries');
      }
      const data = await response.json();

      if (!countOnly) {
        return data as FailedInvoiceIds;
      }
      return {
        identifier: identifier || undefined,
        count: data.length,
      } as CountResponse;
    },
    enabled,
  } as const;
}
